import { useEffect, useState, useCallback } from "react";
import { differenceInYears, differenceInMonths } from "date-fns";
import {
  PawPrint,
  Heart,
  Syringe,
  Bug,
  Brain,
  Phone,
  ChevronLeft,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import PetFormHeader from "@/components/pet-form/PetFormHeader";
import DadosBasicos from "@/components/pet-form/DadosBasicos";
import SaudeCuidados from "@/components/pet-form/SaudeCuidados";
import CarteiraVacinas from "@/components/pet-form/CarteiraVacinas";
import ControleParasitarios from "@/components/pet-form/ControleParasitarios";
import Comportamento from "@/components/pet-form/Comportamento";
import Emergencia from "@/components/pet-form/Emergencia";
import PetTagBox from "@/components/PetTagBox";
import ResumoSaude from "@/components/pet-form/ResumoSaude";
import ImageCropModal from "@/components/ImageCropModal";


export interface PetFormData {
  id?: number;
  nome: string;
  tipo_pet: "cachorro" | "gato" | "outro" | "";
  outro_tipo_pet: string;
  raca: string;
  sexo_pet: "Macho" | "Fêmea" | "";
  data_nascimento: string;
  peso: string;
  idade: string;
  idade_meses: string;

  microchipado: boolean;
  microchip_codigo: string;
  castrado: boolean;

  observacoes: string;

  toma_medicacao: boolean;
  medicacao: string;
  obs_medicacoes: string;

  possui_deficiencia: boolean;
  tipo_deficiencia: string;

  tem_alergia: boolean;
  alergias: string;

  alimentacao_especial: boolean;
  descricao_alimentacao: string;

  doencas: string;

  sociavel_pets: boolean;
  sociavel_criancas: boolean;
  agressivo: boolean;
  medo_chuva: boolean;
  medo_trovao_fogos: boolean;
  dorme_dentro: boolean;
  aceita_outros_pets: boolean;
  ja_ficou_hospedado: boolean;
  precisa_espaco_cercado: boolean;
  precisa_supervisao_constante: boolean;

  horario_refeicao: string;
  horario_passeio: string;
  objetos_essenciais: string;

  emergencia: string;
  contato_medico_vet: string;
  autoriza_vet: boolean;
  autoriza_plantao: boolean;

  vacinas: VacinaEntry[];
  parasitas: ParasitarioEntry[];

  tag_url?: string | null;
  qr_code_path?: string | null;
  foto?: string | null;
  foto_url?: string | null;
}

export type VacinaEntry = {
  id?: number | string;
  vacina_id?: number | null;
  vacina_nome_custom?: string | null;
  nome?: string;
  dose_num?: number | null;
  data_aplicacao?: string | null;
  data_revacinar?: string | null;
  fabricante?: string | null;
  lote?: string | null;
  via?: string | null;
  peso?: string | number | null;
  veterinario?: string | null;
  nome_veterinario?: string | null;
  validado?: boolean | number;
  _delete?: number;
};

export interface ParasitarioEntry {
  id: string;
  tipo: "antipulgas" | "vermifugo" | "";
  produto: string;
  data_aplicacao: string;
  data_reaplicar: string;
  peso?: string;
  dose?: string;
  observacoes?: string;
  validado?: number | boolean;
}

const initialFormData: PetFormData = {
  nome: "",
  tipo_pet: "",
  outro_tipo_pet: "",
  raca: "",
  sexo_pet: "",
  data_nascimento: "",
  peso: "",
  idade: "",
  idade_meses: "",

  microchipado: false,
  microchip_codigo: "",
  castrado: false,

  observacoes: "",

  toma_medicacao: false,
  medicacao: "",
  obs_medicacoes: "",

  possui_deficiencia: false,
  tipo_deficiencia: "",

  tem_alergia: false,
  alergias: "",

  alimentacao_especial: false,
  descricao_alimentacao: "",

  doencas: "",

  sociavel_pets: false,
  sociavel_criancas: false,
  agressivo: false,
  medo_chuva: false,
  medo_trovao_fogos: false,
  dorme_dentro: false,
  aceita_outros_pets: true,
  ja_ficou_hospedado: false,
  precisa_espaco_cercado: false,
  precisa_supervisao_constante: false,

  horario_refeicao: "",
  horario_passeio: "",
  objetos_essenciais: "",

  emergencia: "",
  contato_medico_vet: "",
  autoriza_vet: false,
  autoriza_plantao: false,

  vacinas: [],
  parasitas: [],

  tag_url: null,
  qr_code_path: null,
  foto: null,
  foto_url: null,
};

export function calcularIdade(dataNascimento: string): string {
  if (!dataNascimento) return "";

  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const anos = differenceInYears(hoje, nascimento);
  const meses = differenceInMonths(hoje, nascimento) % 12;

  if (anos > 0) {
    return `${anos} ano${anos > 1 ? "s" : ""}${
      meses > 0 ? ` e ${meses} ${meses > 1 ? "meses" : "mês"}` : ""
    }`;
  }

  if (meses > 0) {
    return `${meses} ${meses > 1 ? "meses" : "mês"}`;
  }

  return "Recém-nascido";
}

function getVacinaLabel(
  tipoPet: PetFormData["tipo_pet"],
  vacinaId?: string | number | null
) {
  const mapa: Record<string, string> = {
    v8: "V8 / V10",
    v3: "V3",
    v4: "V4",
    v5: "V5",
    raiva: "Raiva",
    giardia: "Giárdia",
    gripe: "Gripe",
  };

  if (!vacinaId) return "Vacina";
  return mapa[String(vacinaId)] || String(vacinaId);
}

const EditarPet = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<PetFormData>(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [fotoFile, setFotoFile] = useState<File | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);

  const updateField = useCallback(
    <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        setCropImageSrc(reader.result);
        setShowCropModal(true);
      }
    };

    reader.readAsDataURL(file);
  }

  function handleCropConfirm(file: File) {
    setFotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setShowCropModal(false);
    setCropImageSrc(null);
  }

  function handleCropCancel() {
    setShowCropModal(false);
    setCropImageSrc(null);
  }

  useEffect(() => {
    async function loadPet() {
      try {
        setLoading(true);

        const resp = await fetch(`/react-api/pets/${id}`, {
          headers: { Accept: "application/json" },
          credentials: "include",
        });

        if (!resp.ok) {
          throw new Error("Erro ao carregar pet");
        }

        const data = await resp.json();

        const vacinasNormalizadas: VacinaEntry[] = Array.isArray(data.vacinas)
          ? data.vacinas.map((v: any, index: number) => ({
              id: String(v.id ?? crypto.randomUUID()),
              vacina_id: v.vacina_id ?? null,
              vacina_nome_custom: v.vacina_nome_custom ?? null,
              nome: v.nome ?? "",
              dose_num: Number(v.dose_num ?? index + 1),
              data_aplicacao: v.data_aplicacao ?? "",
              data_revacinar: v.data_revacinar ?? v.proxima_dose ?? "",
              fabricante: v.fabricante ?? v.laboratorio ?? "",
              lote: v.lote ?? "",
              via: v.via ?? "",
              peso: v.peso != null ? String(v.peso) : "",
              veterinario: v.veterinario ?? "",
              nome_veterinario: v.nome_veterinario ?? v.veterinario ?? "",
              validado: Number(v.validado ?? 0),
            }))
          : [];

        const parasitasNormalizados: ParasitarioEntry[] = Array.isArray(data.parasitas)
          ? data.parasitas.map((p: any) => ({
              id: String(p.id ?? crypto.randomUUID()),
              tipo: p.tipo ?? "",
              produto: p.produto ?? "",
              data_aplicacao: p.data_aplicacao ?? "",
              data_reaplicar:
                p.data_reaplicar ?? p.proxima_aplicacao ?? p.data_proxima ?? "",
              peso: p.peso != null ? String(p.peso) : "",
              dose: p.dose ?? "",
              observacoes: p.observacoes ?? "",
              validado: Number(p.validado ?? 0),
            }))
          : [];

        setFormData({
          ...initialFormData,
          id: data.id,
          nome: data.nome || "",
          tipo_pet: data.tipo_pet || "",
          outro_tipo_pet: data.outro_tipo_pet || "",
          raca: data.raca || "",
          sexo_pet: data.sexo_pet || "",
          data_nascimento: data.data_nascimento || "",
          peso: data.peso != null ? String(data.peso) : "",
          idade: data.idade != null ? String(data.idade) : "",
          idade_meses: data.idade_meses != null ? String(data.idade_meses) : "",
          microchipado: !!data.microchipado,
          microchip_codigo: data.microchip_codigo || "",
          castrado: !!data.castrado,
          observacoes: data.observacoes || "",
          toma_medicacao: !!data.toma_medicacao,
          medicacao: data.medicacao || "",
          obs_medicacoes: data.obs_medicacoes || "",
          possui_deficiencia: !!data.possui_deficiencia,
          tipo_deficiencia: data.tipo_deficiencia || "",
          tem_alergia: !!data.tem_alergia,
          alergias: data.alergias || "",
          alimentacao_especial: !!data.alimentacao_especial,
          descricao_alimentacao: data.descricao_alimentacao || "",
          doencas: data.doencas || "",
          sociavel_pets: !!data.sociavel_pets,
          sociavel_criancas: !!data.sociavel_criancas,
          agressivo: !!data.agressivo,
          medo_chuva: !!data.medo_chuva,
          medo_trovao_fogos: !!data.medo_trovao_fogos,
          dorme_dentro: !!data.dorme_dentro,
          aceita_outros_pets: data.aceita_outros_pets ?? true,
          ja_ficou_hospedado: !!data.ja_ficou_hospedado,
          precisa_espaco_cercado: !!data.precisa_espaco_cercado,
          precisa_supervisao_constante: !!data.precisa_supervisao_constante,
          horario_refeicao: data.horario_refeicao || "",
          horario_passeio: data.horario_passeio || "",
          objetos_essenciais: data.objetos_essenciais || "",
          emergencia: data.emergencia || "",
          contato_medico_vet: data.contato_medico_vet || "",
          autoriza_vet: !!data.autoriza_vet,
          autoriza_plantao: !!data.autoriza_plantao,
          vacinas: vacinasNormalizadas,
          parasitas: parasitasNormalizados,
          tag_url: data.tag_url || null,
          qr_code_path: data.qr_code_path || null,
          foto: data.foto || null,
          foto_url: data.foto_url || null,
        });

        if (data.foto_url) {
          setPhotoPreview(data.foto_url);
        } else if (data.foto) {
          setPhotoPreview(
            data.foto.startsWith("http") ? data.foto : `/storage/${data.foto}`
          );
        } else {
          setPhotoPreview(null);
        }
      } catch {
        toast.error("Erro ao carregar os dados do pet.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadPet();
    }
  }, [id]);

  async function salvarVacinas(petId: number | string) {
    const token = (
      document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
    )?.content;

    for (const vacina of formData.vacinas) {
      const temNomeCustom =
        vacina.vacina_id == null &&
        typeof vacina.vacina_nome_custom === "string" &&
        vacina.vacina_nome_custom.trim() !== "";

      if ((!vacina.vacina_id && !temNomeCustom) || !vacina.data_aplicacao) {
        continue;
      }

      const response = await fetch(`/pets/${petId}/vacinas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          vacina_id: vacina.vacina_id ?? null,
          vacina_nome_custom: temNomeCustom
            ? vacina.vacina_nome_custom?.trim()
            : null,
          dose_num: vacina.dose_num || 1,
          data_aplicacao: vacina.data_aplicacao,
          data_revacinar: vacina.data_revacinar || null,
          fabricante: vacina.fabricante || "",
          lote: vacina.lote || "",
          via: vacina.via || "",
          veterinario: vacina.nome_veterinario || vacina.veterinario || "",
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Erro ao salvar vacinas.");
      }
    }
  }

  async function salvarParasitarios(petId: number | string) {
    const token = (
      document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
    )?.content;

    for (const parasita of formData.parasitas) {
      if (!parasita.tipo || !parasita.data_aplicacao) continue;

      const response = await fetch(`/pets/${petId}/parasitas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          tipo: parasita.tipo,
          produto: parasita.produto || "",
          data_aplicacao: parasita.data_aplicacao,
          data_reaplicar: parasita.data_reaplicar || null,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Erro ao salvar parasitários.");
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = new FormData();
      payload.append("_method", "PUT");

      Object.entries(formData).forEach(([key, value]) => {
        if (
          key === "foto" ||
          key === "foto_url" ||
          key === "id" ||
          key === "vacinas" ||
          key === "parasitas" ||
          key === "tag_url" ||
          key === "qr_code_path"
        ) {
          return;
        }

        if (value === null || value === undefined) return;

        if (typeof value === "boolean") {
          payload.append(key, value ? "1" : "0");
          return;
        }

        payload.append(key, String(value));
      });

      if (fotoFile) {
        payload.append("foto", fotoFile);
      }

      const token = (
        document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
      )?.content;

      const response = await fetch(`/react-api/pets/${id}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: payload,
      });

      let data: any = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        throw new Error(data?.message || "Não foi possível salvar o pet.");
      }

      const petIdSalvo = data?.pet?.id || data?.id || formData.id || id;

      await salvarVacinas(petIdSalvo);
      await salvarParasitarios(petIdSalvo);

      toast.success("Pet atualizado com sucesso.");
      window.location.href = "/tutor/pets";
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Erro ao salvar pet.");
    } finally {
      setSaving(false);
    }
  }


  const hoje = new Date();
hoje.setHours(0, 0, 0, 0);

function dataValidaIso(data?: string | null) {
  return (
    typeof data === "string" &&
    data.trim() !== "" &&
    data !== "0000-00-00" &&
    !isNaN(new Date(`${data}T00:00:00`).getTime())
  );
}

function chaveVacina(v: VacinaEntry) {
  if (typeof v.vacina_nome_custom === "string" && v.vacina_nome_custom.trim() !== "") {
    return `custom:${v.vacina_nome_custom.trim().toLowerCase()}`;
  }

  if (v.vacina_id != null) {
    return `id:${String(v.vacina_id)}`;
  }

  if (typeof v.nome === "string" && v.nome.trim() !== "") {
    return `nome:${v.nome.trim().toLowerCase()}`;
  }

  return null;
}

function chaveParasita(p: ParasitarioEntry) {
  const tipo = typeof p.tipo === "string" ? p.tipo.trim().toLowerCase() : "";
  if (!tipo) return null;
  return tipo;
}

const vacinasMap = new Map<string, VacinaEntry>();

formData.vacinas.forEach((v) => {
  const chave = chaveVacina(v);
  if (!chave || !dataValidaIso(v.data_revacinar)) return;

  const atualMs = new Date(`${v.data_revacinar}T00:00:00`).getTime();
  const existente = vacinasMap.get(chave);

  if (!existente) {
    vacinasMap.set(chave, v);
    return;
  }

  const existenteMs = new Date(`${existente.data_revacinar}T00:00:00`).getTime();

  if (atualMs > existenteMs) {
    vacinasMap.set(chave, v);
  }
});

const parasitasMap = new Map<string, ParasitarioEntry>();

formData.parasitas.forEach((p) => {
  const chave = chaveParasita(p);
  if (!chave || !dataValidaIso(p.data_reaplicar)) return;

  const atualMs = new Date(`${p.data_reaplicar}T00:00:00`).getTime();
  const existente = parasitasMap.get(chave);

  if (!existente) {
    parasitasMap.set(chave, p);
    return;
  }

  const existenteMs = new Date(`${existente.data_reaplicar}T00:00:00`).getTime();

  if (atualMs > existenteMs) {
    parasitasMap.set(chave, p);
  }
});

formData.parasitas.forEach((p) => {
  const chave = chaveParasita(p);
  if (!chave || !dataValidaIso(p.data_reaplicar)) return;

  const atualMs = new Date(`${p.data_reaplicar}T00:00:00`).getTime();
  const existente = parasitasMap.get(chave);

  if (!existente) {
    parasitasMap.set(chave, p);
    return;
  }

  const existenteMs = new Date(`${existente.data_reaplicar}T00:00:00`).getTime();

  if (atualMs > existenteMs) {
    parasitasMap.set(chave, p);
  }
});

const vacinasValidas = Array.from(vacinasMap.values());
const parasitasValidos = Array.from(parasitasMap.values());

let vacAtrasadas = 0;
let vacProximas = 0;
let vacEmDia = 0;

vacinasValidas.forEach((v) => {
  const data = new Date(`${v.data_revacinar}T00:00:00`);
  const diff = Math.floor((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  if (data < hoje) vacAtrasadas++;
  else if (diff <= 30) vacProximas++;
  else vacEmDia++;
});

let parAtrasados = 0;
let parProximos = 0;
let parEmDia = 0;

parasitasValidos.forEach((p) => {
  const data = new Date(`${p.data_reaplicar}T00:00:00`);
  const diff = Math.floor((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  if (data < hoje) parAtrasados++;
  else if (diff <= 30) parProximos++;
  else parEmDia++;
});

const totalVacinasValidas = vacinasValidas.length;
const totalParasitasValidos = parasitasValidos.length;
const totalRegistrosValidos = totalVacinasValidas + totalParasitasValidos;

let statusGeral = "Sem dados";
let statusClass: "success" | "warning" | "danger" | "secondary" = "secondary";

if (totalRegistrosValidos > 0) {
  if (vacAtrasadas > 0 || parAtrasados > 0) {
    statusGeral = "Atrasadas";
    statusClass = "danger";
  } else if (vacProximas > 0 || parProximos > 0) {
    statusGeral = "Próximas";
    statusClass = "warning";
  } else {
    statusGeral = "Em dia";
    statusClass = "success";
  }
}

const apto =
  totalRegistrosValidos > 0 &&
  vacAtrasadas === 0 &&
  parAtrasados === 0;

let proximoNome = "";
let proximaDataMs: number | null = null;

vacinasValidas.forEach((v) => {
  const dataMs = new Date(`${v.data_revacinar}T00:00:00`).getTime();

  if (proximaDataMs === null || dataMs < proximaDataMs) {
    proximaDataMs = dataMs;
    proximoNome = v.vacina_nome_custom?.trim()
      ? `Vacina: ${v.vacina_nome_custom.trim()}`
      : `Vacina: ${getVacinaLabel(formData.tipo_pet, v.vacina_id)}`;
  }
});

parasitasValidos.forEach((p) => {
  const dataMs = new Date(`${p.data_reaplicar}T00:00:00`).getTime();

  if (proximaDataMs === null || dataMs < proximaDataMs) {
    proximaDataMs = dataMs;
    proximoNome = `Parasitário: ${p.produto || p.tipo || "Produto"}`;
  }
});

let proximo: { nome: string; data: string } | null = null;

if (proximaDataMs !== null) {
  const data = new Date(proximaDataMs);
  const dia = String(data.getDate()).padStart(2, "0");
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const ano = data.getFullYear();

  proximo = {
    nome: proximoNome || "Evento",
    data: `${dia}/${mes}/${ano}`,
  };
}

  const idade = calcularIdade(formData.data_nascimento);

  const sections = [
    { icon: PawPrint, title: "Dados Básicos" },
    { icon: Heart, title: "Saúde & Cuidados" },
    { icon: Syringe, title: "Carteira de Vacinas" },
    { icon: Bug, title: "Controle de Parasitários" },
    { icon: Brain, title: "Comportamento" },
    { icon: Phone, title: "Emergência" },
  ];

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }


  return (
    <div className="min-h-screen gradient-hero">
      <div className="container max-w-4xl py-6 px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 animate-fade-in-up">
          <button
            type="button"
            onClick={() => {
              window.location.href = "/tutor/pets";
            }}
            className="hover:text-forest transition-smooth"
          >
            Início
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/tutor/pets";
            }}
            className="hover:text-forest transition-smooth"
          >
            Meus Pets
          </button>
          <span>/</span>
          <span className="text-forest font-semibold">Editar Pet</span>
        </nav>

        <PetFormHeader
          photoPreview={photoPreview}
          handlePhotoChange={handlePhotoChange}
          petName={formData.nome}
          isEditing={true}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="animate-fade-in-up stagger-1 opacity-0" style={{ animationFillMode: "forwards" }}>
            <SectionCard icon={sections[0].icon} title={sections[0].title}>
              <DadosBasicos
                formData={formData}
                updateField={updateField}
                idade={idade}
                photoPreview={photoPreview}
                handlePhotoChange={handlePhotoChange}
              />
            </SectionCard>
          </section>

          <section className="animate-fade-in-up stagger-2 opacity-0" style={{ animationFillMode: "forwards" }}>
            <SectionCard icon={sections[1].icon} title={sections[1].title}>
              <SaudeCuidados formData={formData} updateField={updateField} />
            </SectionCard>
          </section>

          <section className="animate-fade-in-up stagger-3 opacity-0" style={{ animationFillMode: "forwards" }}>
            <div className="mb-4">
              <ResumoSaude
                statusGeral={statusGeral}
                statusClass={statusClass}
                vacinas={{
                  atrasadas: vacAtrasadas,
                  proximas: vacProximas,
                  emDia: vacEmDia,
                }}
                parasitas={{
                  atrasados: parAtrasados,
                  proximos: parProximos,
                  emDia: parEmDia,
                }}
                proximo={proximo}
                apto={apto}
              />
            </div>

            <SectionCard icon={sections[2].icon} title={sections[2].title}>
              <CarteiraVacinas formData={formData} updateField={updateField} />
            </SectionCard>
          </section>

          <section className="animate-fade-in-up stagger-4 opacity-0" style={{ animationFillMode: "forwards" }}>
            <SectionCard icon={sections[3].icon} title={sections[3].title}>
              <ControleParasitarios formData={formData} updateField={updateField} />
            </SectionCard>
          </section>

          <section className="animate-fade-in-up stagger-5 opacity-0" style={{ animationFillMode: "forwards" }}>
            <SectionCard icon={sections[4].icon} title={sections[4].title}>
              <Comportamento formData={formData} updateField={updateField} />
            </SectionCard>
          </section>

          <section className="animate-fade-in-up stagger-6 opacity-0" style={{ animationFillMode: "forwards" }}>
            <SectionCard icon={sections[5].icon} title={sections[5].title}>
              <Emergencia formData={formData} updateField={updateField} />
            </SectionCard>
          </section>

          <PetTagBox
            nome={formData.nome}
            tagUrl={formData.tag_url}
            qrCodePath={formData.qr_code_path}
          />

          <div className="flex justify-end gap-3 pt-4 pb-8 animate-fade-in-up stagger-7 opacity-0" style={{ animationFillMode: "forwards" }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.location.href = "/tutor/pets";
              }}
              className="gap-2"
              disabled={saving}
            >
              <ChevronLeft className="w-4 h-4" />
              Cancelar
            </Button>

            <Button
              type="submit"
              className="gradient-primary gap-2 text-primary-foreground hover:opacity-90 transition-smooth"
              disabled={saving}
            >
              <Save className="w-4 h-4" />
              {saving ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </div>

      {showCropModal && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          aspect={1}
          outputWidth={800}
          outputHeight={800}
          title="Ajustar foto do pet"
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
};

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card rounded-xl card-shadow overflow-hidden">
      <div className="gradient-primary px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-foreground/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary-foreground" />
        </div>
        <h2 className="text-lg font-bold text-primary-foreground">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default EditarPet;